/** The 2D context calls an export makes. A `CanvasRenderingContext2D` satisfies it. */
export interface ExportContext {
    fillStyle: CanvasRenderingContext2D['fillStyle'];
    fillRect(x: number, y: number, width: number, height: number): void;
    drawImage(image: HTMLCanvasElement, dx: number, dy: number): void;
}

/** The canvas an export is composed on. An `HTMLCanvasElement` satisfies it. */
export interface ExportCanvas {
    width: number;
    height: number;
    getContext(contextId: '2d'): ExportContext | null;
    toBlob(callback: BlobCallback, type?: string): void;
}

function createExportCanvas(): ExportCanvas {
    return document.createElement('canvas');
}

/**
 * The chart on `source` as a PNG on an opaque `background`.
 *
 * A Chart.js canvas is transparent wherever nothing is drawn, so a PNG straight from it shows
 * whatever the viewer puts behind it: dark-theme text and gridlines vanish on a white page,
 * and a light chart turns into floating lines on a dark one. The chart is composed onto a
 * copy filled with the chart's surface instead, so the image looks as it did on screen. The
 * on-screen canvas is left untouched.
 */
export function chartImageBlob(
    source: HTMLCanvasElement,
    background: string,
    onBlob: BlobCallback,
    createCanvas: () => ExportCanvas = createExportCanvas
): void {
    const canvas = createCanvas();
    canvas.width = source.width;
    canvas.height = source.height;
    const context = canvas.getContext('2d');
    if (!context) {
        // No 2D context to compose on: the transparent image beats no image.
        source.toBlob(onBlob, 'image/png');
        return;
    }
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(source, 0, 0);
    canvas.toBlob(onBlob, 'image/png');
}
